import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bugReportAPI } from '../lib/api';
import { Bug, Loader2, CheckCircle, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function BugReportPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', description: '', severity: 'medium',
    steps_to_reproduce: '', expected_behavior: '', actual_behavior: '',
    page_url: typeof window !== 'undefined' ? window.location.origin : '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  React.useEffect(() => {
    if (!authLoading && !user) navigate('/login');
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description) {
      toast.error('Title and description are required');
      return;
    }
    setLoading(true);
    try {
      await bugReportAPI.submit({
        ...form,
        browser_info: navigator.userAgent,
      });
      setSubmitted(true);
      toast.success('Bug report submitted! We\'ll look into it.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
    setLoading(false);
  };

  if (authLoading) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  if (submitted) {
    return (
      <div className="px-6 md:px-12 max-w-7xl mx-auto py-20">
        <div className="max-w-lg mx-auto bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center" data-testid="bug-report-success">
          <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
          <p className="text-zinc-500 mb-6">Your bug report has been submitted. Our team will review it and notify you of any updates.</p>
          <div className="flex justify-center gap-3">
            <Button onClick={() => { setSubmitted(false); setForm({ title: '', description: '', severity: 'medium', steps_to_reproduce: '', expected_behavior: '', actual_behavior: '', page_url: '' }); }}
              variant="outline" className="border-zinc-800 text-zinc-400 rounded-sm" data-testid="report-another-btn">
              Report Another
            </Button>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#00E5FF] text-black font-bold rounded-sm" data-testid="back-to-dashboard-btn">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-12 max-w-4xl mx-auto py-12 md:py-20" data-testid="bug-report-page">
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-8">
        <Bug className="h-6 w-6 text-red-400" />
        <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">Report a Bug</h1>
      </div>

      <div className="bg-[#0A0A0A] border border-amber-500/20 rounded-sm p-4 mb-8 flex items-start gap-3" data-testid="bug-report-info">
        <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-zinc-300">Help us improve XDomainX by reporting issues you encounter.</p>
          <p className="text-xs text-zinc-500 mt-1">Please provide as much detail as possible so we can reproduce and fix the issue.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-6 space-y-5" data-testid="bug-report-form">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-3">
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Bug Title *</Label>
            <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))} required
              className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm"
              placeholder="Brief summary of the issue" data-testid="bug-title" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Severity</Label>
            <Select value={form.severity} onValueChange={(v) => setForm(p => ({ ...p, severity: v }))}>
              <SelectTrigger className="mt-1.5 h-11 bg-[#050505] border-zinc-800 text-white rounded-sm" data-testid="bug-severity"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="low" className="text-zinc-300">Low</SelectItem>
                <SelectItem value="medium" className="text-zinc-300">Medium</SelectItem>
                <SelectItem value="high" className="text-zinc-300">High</SelectItem>
                <SelectItem value="critical" className="text-zinc-300">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">Description *</Label>
          <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} required
            rows={4} className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none"
            placeholder="Describe the bug in detail" data-testid="bug-description" />
        </div>

        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">Steps to Reproduce</Label>
          <Textarea value={form.steps_to_reproduce} onChange={(e) => setForm(p => ({ ...p, steps_to_reproduce: e.target.value }))}
            rows={3} className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none"
            placeholder="1. Go to...&#10;2. Click on...&#10;3. See error" data-testid="bug-steps" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Expected Behavior</Label>
            <Textarea value={form.expected_behavior} onChange={(e) => setForm(p => ({ ...p, expected_behavior: e.target.value }))}
              rows={2} className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none"
              placeholder="What should happen?" data-testid="bug-expected" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs uppercase tracking-wider">Actual Behavior</Label>
            <Textarea value={form.actual_behavior} onChange={(e) => setForm(p => ({ ...p, actual_behavior: e.target.value }))}
              rows={2} className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm resize-none"
              placeholder="What actually happened?" data-testid="bug-actual" />
          </div>
        </div>

        <div>
          <Label className="text-zinc-400 text-xs uppercase tracking-wider">Page URL (where the bug occurred)</Label>
          <Input value={form.page_url} onChange={(e) => setForm(p => ({ ...p, page_url: e.target.value }))}
            className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm"
            placeholder="https://xdomainx.com/..." data-testid="bug-url" />
        </div>

        <Button type="submit" disabled={loading}
          className="h-11 px-8 bg-red-500 text-white hover:bg-red-400 font-bold uppercase tracking-wider rounded-sm" data-testid="bug-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Bug className="h-4 w-4 mr-2" />}
          Submit Bug Report
        </Button>
      </form>
    </div>
  );
}
